/**
 * Centralized Frontend Logger.
 *
 * Routing rules:
 *
 * | Level | Dev console | Grafana (prod) | Faro pushError | Sentry SDK |
 * |-------|-------------|----------------|----------------|------------|
 * | debug | Yes         | No             | No             | No         |
 * | info  | Yes         | Yes (log)      | No             | No         |
 * | warn  | Yes         | Yes (log)      | No             | No         |
 * | error | Yes         | Yes (log)      | Yes (if error) | Yes (if error) |
 *
 * Usage:
 *   import { log } from "@/modules/shared/lib/observability";
 *   log.info("Deal synced", { dealId: "123" });
 *   log.error("Sync failed", { provider: "hubspot" }, error);
 */
import { captureError } from "./grafana";
import { pushLog } from "./grafana";
import { captureSentryError } from "./sentry";
import { observabilityConfig } from "./env";

function toStringRecord(obj?: Record<string, unknown>): Record<string, string> | undefined {
  if (!obj) return undefined;
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null) result[k] = String(v);
  }
  return result;
}

export const log = {
  /** Debug: dev console only, never sent externally. */
  debug(message: string, context?: Record<string, unknown>): void {
    if (!observabilityConfig.isProduction) {
      console.debug(`[debug] ${message}`, context || "");
    }
  },

  /** Info: dev console + Grafana pushLog INFO. */
  info(message: string, context?: Record<string, unknown>): void {
    if (!observabilityConfig.isProduction) {
      console.log(`[info] ${message}`, context || "");
    }
    pushLog("info", message, toStringRecord(context));
  },

  /** Warn: dev console + Grafana pushLog WARN. */
  warn(message: string, context?: Record<string, unknown>): void {
    if (!observabilityConfig.isProduction) {
      console.warn(`[warn] ${message}`, context || "");
    }
    pushLog("warn", message, toStringRecord(context));
  },

  /** Error: dev console + Grafana pushLog ERROR + captureError + Sentry SDK if error provided. */
  error(message: string, context?: Record<string, unknown>, error?: unknown): void {
    if (!observabilityConfig.isProduction) {
      console.error(`[error] ${message}`, context || "", error || "");
    }
    pushLog("error", message, toStringRecord(context));
    if (error !== undefined) {
      captureError(error, { message, ...context });
      captureSentryError(error, { message, ...toStringRecord(context) });
    }
  },
};
